import {
  Alert,
  AlertColor,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  LinearProgress,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import { useMutation, useQueryClient } from 'react-query'
import { AxiosResponse } from 'axios'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useUserContext } from '../../../Contexts/UserConfig'
import RenderError from '../../../garvan-react/Components/ModalFeedback/RenderError'
import PyAPI from '../../../APIs/Python/APIs/PyAPI'

function NewPhaseModal({ open, setOpen }: { open: boolean; setOpen: any }) {
  const { whoAmI } = useUserContext()
  const queryClient = useQueryClient()
  const { study_id } = useParams()
  const [attrStatus, setAttrStatus] = useState({})

  const createAttribute = useMutation<any, AxiosResponse, any, () => void>(
    ({ type, phase_id }) =>
      PyAPI.put(
        whoAmI,
        `/study/${study_id}/phase/${phase_id}/attribute?data_type=${type}`,
      ),
    {
      onSuccess: (data, variables) => {
        setAttrStatus((a) => {
          return {
            ...a,
            [`${variables.group}_${variables.type}`]: data.AttrID,
          }
        })
      },
      onError: (_, variables) => {
        setAttrStatus((a) => {
          return {
            ...a,
            [`${variables.group}_${variables.type}`]: 'FAIL',
          }
        })
        setMessage('An attribute could not be created')
        setSeverity('error')
      },
    },
  )

  const editAttribute = useMutation<any, AxiosResponse, any, () => void>(
    ({ attrId, patchFields, phase_id }) =>
      PyAPI.patch(
        whoAmI,
        `/study/${study_id}/phase/${phase_id}/attribute?attr_id=${attrId}`,
        patchFields,
      ),
    {
      onError: (_, variables) => {
        setAttrStatus((a) => {
          return {
            ...a,
            [`${variables.group}_${variables.type}`]: 'FAIL',
          }
        })
        setMessage('An attribute could not be updated')
        setSeverity('error')
      },
    },
  )

  const {
    mutate: addPhase,
    error,
    isLoading,
    reset,
  } = useMutation<any, AxiosResponse>(
    () => PyAPI.put(whoAmI, `/study/${study_id}/phase`),
    {
      onSuccess: async (data) => {
        setStep((s) => s + 1)
        if (weight || tumour) {
          setMessage('Creating Attributes')
          //Creation of attributes should be done sequentially to prevent conflict of id
          //Parallel async cause some to fail
          const weightAttCreateDec = await createAttribute.mutateAsync({
            type: 'DEC',
            group: 'weight',
            phase_id: data?.PhaseID,
          })
          let weightAttCreateCal
          let weightAttCreateEnd
          if (weight) {
            weightAttCreateCal = await createAttribute.mutateAsync({
              type: 'CAL',
              group: 'weight',
              phase_id: data?.PhaseID,
            })
            weightAttCreateEnd = await createAttribute.mutateAsync({
              type: 'END',
              group: 'weight',
              phase_id: data?.PhaseID,
            })
          }
          let tumourAttCreateDecLen
          let tumourAttCreateDecWid
          let tumourAttCreateCal
          let tumourAttCreateEnd
          if (tumour) {
            tumourAttCreateDecLen = await createAttribute.mutateAsync({
              type: 'DEC',
              group: 'tumour',
              phase_id: data?.PhaseID,
            })
            tumourAttCreateDecWid = await createAttribute.mutateAsync({
              type: 'DEC',
              group: 'tumour',
              phase_id: data?.PhaseID,
            })
            tumourAttCreateCal = await createAttribute.mutateAsync({
              type: 'CAL',
              group: 'tumour',
              phase_id: data?.PhaseID,
            })
            tumourAttCreateEnd = await createAttribute.mutateAsync({
              type: 'END',
              group: 'tumour',
              phase_id: data?.PhaseID,
            })
          }

          setMessage('Defining Attributes')
          const attUpdateDec =
            weightAttCreateDec?.AttrID ?
              await editAttribute.mutateAsync({
                attrId: weightAttCreateDec?.AttrID,
                patchFields: {
                  AttrName: 'Weight',
                },
                type: 'DEC',
                group: 'weight',
                phase_id: data?.PhaseID,
              })
            : {}

          if (weight) {
            const attUpdateCal =
              weightAttCreateCal?.AttrID && attUpdateDec?.AttrID ?
                await editAttribute.mutateAsync({
                  attrId: weightAttCreateCal?.AttrID,
                  patchFields: {
                    AttrName: 'Weight Loss',
                    Formula: `=(MAX(U%UAI%P${data?.PhaseID}A${weightAttCreateDec?.AttrID})-newU%UAI%P${data?.PhaseID}A${weightAttCreateDec?.AttrID})/MAX(U%UAI%P${data?.PhaseID}A${weightAttCreateDec?.AttrID})`,
                  },
                  type: 'CAL',
                  group: 'weight',
                  phase_id: data?.PhaseID,
                })
              : {}
            await editAttribute.mutateAsync({
              attrId: weightAttCreateEnd?.AttrID,
              patchFields: {
                AttrName: 'Weight Loss Endpoint',
                Formula: `=newU%UAI%P${data?.PhaseID}A${attUpdateCal?.AttrID}>=0.2`,
              },
              type: 'END',
              group: 'weight',
              phase_id: data?.PhaseID,
            })
          }

          if (tumour) {
            const attUpdateDecLen =
              tumourAttCreateDecLen?.AttrID ?
                await editAttribute.mutateAsync({
                  attrId: tumourAttCreateDecLen?.AttrID,
                  patchFields: {
                    AttrName: 'Tumour Length',
                  },
                  type: 'DEC',
                  group: 'tumour',
                  phase_id: data?.PhaseID,
                })
              : {}
            const attUpdateDecWid =
              tumourAttCreateDecWid?.AttrID ?
                await editAttribute.mutateAsync({
                  attrId: tumourAttCreateDecWid?.AttrID,
                  patchFields: {
                    AttrName: 'Tumour Width',
                  },
                  type: 'DEC',
                  group: 'tumour',
                  phase_id: data?.PhaseID,
                })
              : {}
            const attUpdateCal =
              (
                tumourAttCreateCal?.AttrID &&
                attUpdateDecLen?.AttrID &&
                attUpdateDecWid?.AttrID
              ) ?
                await editAttribute.mutateAsync({
                  attrId: tumourAttCreateCal?.AttrID,
                  patchFields: {
                    AttrName: 'Tumour Volume',
                    Formula: `=newU%UAI%P${data?.PhaseID}A${attUpdateDecLen?.AttrID}*newU%UAI%P${data?.PhaseID}A${attUpdateDecWid?.AttrID}`,
                  },
                  type: 'CAL',
                  group: 'tumour',
                  phase_id: data?.PhaseID,
                })
              : {}
            await editAttribute.mutateAsync({
              attrId: tumourAttCreateEnd?.AttrID,
              patchFields: {
                AttrName: 'Tumour Endpoint',
                Formula: `=(newU%UAI%P${data?.PhaseID}A${attUpdateCal?.AttrID})/2 > (newU%UAI%P${data?.PhaseID}A${weightAttCreateDec?.AttrID} * 0.1)`,
              },
              type: 'END',
              group: 'tumour',
              phase_id: data?.PhaseID,
            })
          }
          if (Object.values(attrStatus).includes('FAIL')) {
            setSeverity('error')
            setMessage('An error occurred')
          } else {
            setSeverity('success')
            setMessage('Completed Successfully')
          }
          setStep((s) => s + 1)
        } else {
          setMessage('Completed Successfully')
          setSeverity('success')
        }
        await queryClient.invalidateQueries({
          queryKey: ['PyAPI', '/study'],
        })
        reset()
      },
      onError: () => {
        setSeverity('error')
      },
    },
  )

  const [weight, setWeight] = useState(false)
  const [tumour, setTumour] = useState(false)
  const [step, setStep] = useState(0)

  const add = ['Add Attributes']
  const insert = weight || tumour ? add : []

  const steps = ['Configure', 'Create', ...insert, 'Complete']

  const [severity, setSeverity] = useState<AlertColor>('info')
  const loading = isLoading
  const [message, setMessage] = useState('Creating Phase')

  function stepErrorHelper(index: number) {
    switch (index) {
      case 1:
        return (
          <Typography sx={{ mb: 1 }}>
            The phase has <strong>not</strong> been created yet. See below error
            for details.
          </Typography>
        )
      case 2:
        return (
          <Typography sx={{ mb: 1 }}>
            The phase was created in last step, but an attribute has failed. See
            below error for details.
          </Typography>
        )
      default:
        return undefined
    }
  }

  function calcError(index: number) {
    switch (index) {
      case 1:
        return index <= step ? !!error : false
      case 2:
        return index <= step ?
            Object.values(attrStatus).includes('FAIL')
          : false
      default:
        return false
    }
  }

  return (
    <Dialog open={open}>
      <DialogTitle>{'Add a new Phase'}</DialogTitle>
      <DialogContent
        sx={{
          maxHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 550,
        }}
      >
        <Stepper
          activeStep={step}
          alternativeLabel
          sx={{ mb: 2 }}
          color={'success'}
        >
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                error={calcError(index)}
                sx={{
                  '& .Mui-completed': {
                    color: (theme) =>
                      theme.palette.success.main + ' !important',
                  },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        {step === 0 ?
          <>
            <Typography sx={{ mb: 1 / 2 }}>
              Would you like to include:
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={weight}
                  onChange={(e) => setWeight(e.target.checked)}
                  color={'primary'}
                />
              }
              label={
                <Typography>
                  <i>Percentage Weight Loss</i> attributes?
                </Typography>
              }
              labelPlacement="end"
            />
            <FormHelperText>
              This will add <i>Weight</i>, <i>Weight Loss</i>, and{' '}
              <i>Weight Loss Endpoint</i> attributes
            </FormHelperText>
            <FormControlLabel
              control={
                <Checkbox
                  checked={tumour}
                  onChange={(e) => setTumour(e.target.checked)}
                  color={'primary'}
                />
              }
              label={
                <Typography>
                  <i>Tumour Size</i> attributes?
                </Typography>
              }
              labelPlacement="end"
            />
            <FormHelperText>
              This will add <i>Weight</i>, <i>Tumour Length</i>,{' '}
              <i>Tumour Width</i>, <i>Tumour Volume</i>, and{' '}
              <i>Tumour Endpoint</i> attributes
            </FormHelperText>
            <Box
              sx={{
                mt: 1 / 2,
                width: 1,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Button
                variant={'contained'}
                onClick={() => {
                  setStep(1)
                  addPhase()
                }}
              >
                {'Create Phase'}
              </Button>
            </Box>
          </>
        : <>
            {error ?
              <>
                {stepErrorHelper(step)}
                <RenderError error={error} />
              </>
            : <Alert
                severity={severity}
                sx={{
                  '& .MuiAlert-message': {
                    width: '100%',
                  },
                }}
              >
                <Box sx={{ width: 1 }}>
                  <AlertTitle>{message}</AlertTitle>
                  {loading && (
                    <LinearProgress
                      color={severity}
                      sx={{ mt: 2 }}
                    />
                  )}
                </Box>
              </Alert>
            }
          </>
        }
      </DialogContent>
      <DialogActions>
        <Button
          disabled={
            step > 0 && !error && !Object.values(attrStatus).includes('FAIL')
          }
          color={'error'}
          onClick={() => {
            setOpen(false)
            setStep(0)
            setMessage('Creating Phase')
            setSeverity('info')
            setAttrStatus({})
          }}
        >
          Cancel
        </Button>
        <Button
          disabled={step !== steps.length - 1}
          onClick={() => {
            setOpen(false)
            setStep(0)
            setMessage('Creating Phase')
            setSeverity('info')
            setAttrStatus({})
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default NewPhaseModal
